/**
 * Store → Products → Bulk edit: spreadsheet over one page of products (100 rows). Arrows/Tab/Enter move, typing edits,
 * paste a TSV block at the focused cell; Save sends only the changed cells (PATCH /products/bulk), all or nothing.
 * edits hold the cell TEXT ({id: {field: text}}) and are converted when saving; only changed cells stay in there.
 */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Empty, Input, Pagination, Select, Space, Spin, Tooltip, Typography, message } from 'antd';
import { ArrowLeftOutlined, ExportOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { QUIET } from './api';
import { StatusTag, confirmLeave, currencyDigits, fromMinor, listOf, sitePath, toMinor, toastErr, totalOf, useLeaveGuard, useSite } from './common';

const { Text } = Typography;
const PER_PAGE = 100;
const STATUSES = ['active', 'draft', 'archived'];
const COLS = [
    { key: 'title', width: 260, label: 'Title' },
    { key: 'status', width: 100, label: 'Status' },
    { key: 'price', width: 110, label: 'Price', align: 'right', money: true },
    { key: 'compare_at_price', width: 140, label: 'Compare-at', align: 'right', money: true },
    { key: 'inventory_quantity', width: 90, label: 'Inventory', align: 'right' },
    { key: 'sku', width: 130, label: 'SKU' },
    { key: 'tags', width: 170, label: 'Tags' },
    { key: 'vendor', width: 130, label: 'Vendor' },
    { key: 'product_type', width: 130, label: 'Type' },
];
const LEAD = 34; // thumbnail/link column

const moneyText = (v, cur) => (v === null || v === undefined ? '' : Number(fromMinor(v, cur)).toFixed(currencyDigits(cur)));
/** a mixed price has no single text: empty until something is typed, which then applies to every variant */
const cellText = (row, f, cur) => {
    switch (f) {
        case 'price': return row.price_mixed ? '' : moneyText(row.price, cur);
        case 'compare_at_price': return row.compare_at_mixed ? '' : moneyText(row.compare_at_price, cur);
        case 'inventory_quantity': return String(row.inventory_quantity ?? 0);
        case 'tags': return (Array.isArray(row.tags) ? row.tags : String(row.tags || '').split(',')).map((x) => String(x).trim()).filter(Boolean).join(', ');
        default: return String(row[f] ?? '');
    }
};
const readOnlyReason = (row, f) => {
    if ((f === 'sku' || f === 'inventory_quantity') && row.variants_count > 1) return 'has_variants';
    if (f === 'inventory_quantity' && !row.track_inventory) return 'not_tracked';
    return null;
};
const parseMoney = (s) => {
    const x = String(s).replace(/[^\d.,-]/g, '');
    if (!x) return NaN;
    // "1,234.50" / "1.234,50" / "12,5": the last separator is the decimal one
    const last = Math.max(x.lastIndexOf('.'), x.lastIndexOf(','));
    const tail = last >= 0 ? x.slice(last + 1) : '';
    const norm = last >= 0 && tail.length !== 3 ? `${x.slice(0, last).replace(/[.,]/g, '')}.${tail}` : x.replace(/[.,]/g, '');
    return Number(norm);
};
/** cell text → API value, or undefined + code when it cannot be saved */
const toApi = (row, f, text, cur) => {
    const s = String(text).trim();
    switch (f) {
        case 'title': return s ? { value: s } : { code: 'Title is required' };
        case 'status': return STATUSES.includes(s) ? { value: s } : { code: 'Invalid status' };
        case 'price': case 'compare_at_price': {
            if (!s) return f === 'price' ? { code: 'Price is required' } : { value: null };
            const n = parseMoney(s);
            return Number.isFinite(n) && n >= 0 ? { value: toMinor(n, cur) } : { code: 'Enter a valid amount' };
        }
        case 'inventory_quantity': return /^-?\d+$/.test(s) ? { value: Number(s) } : { code: 'Inventory must be a whole number' };
        case 'tags': return { value: s.split(',').map((x) => x.trim()).filter(Boolean) };
        default: return { value: s };
    }
};
/** Excel/Sheets clipboard text → rows of cells (quoted cells may hold tabs and newlines) */
export const parseTsv = (txt) => {
    const rows = [[]];
    let cell = '';
    let q = false;
    const s = String(txt).replace(/\r\n?/g, '\n').replace(/\n$/, '');
    for (let i = 0; i < s.length; i += 1) {
        const c = s[i];
        if (q) {
            if (c === '"' && s[i + 1] === '"') { cell += '"'; i += 1; } else if (c === '"') q = false; else cell += c;
        } else if (c === '"' && cell === '') q = true;
        else if (c === '\t') { rows[rows.length - 1].push(cell); cell = ''; } else if (c === '\n') { rows[rows.length - 1].push(cell); rows.push([]); cell = ''; } else cell += c;
    }
    rows[rows.length - 1].push(cell);
    return rows;
};
const STYLE = `
.yc-bulk{outline:none;overflow:auto;max-height:calc(100vh - 250px);min-height:240px;border:1px solid var(--ant-color-border-secondary,#f0f0f0);border-radius:6px;scroll-padding:34px 0 0 ${LEAD + COLS[0].width}px}
.yc-bulk table{border-collapse:separate;border-spacing:0;table-layout:fixed;font-size:13px}
.yc-bulk th{position:sticky;top:0;z-index:2;background:var(--ant-color-bg-elevated,#fff);font-weight:500;text-align:start;padding:6px 8px;color:var(--ant-color-text-tertiary,#888);border-bottom:1px solid var(--ant-color-border,#ddd);white-space:nowrap}
.yc-bulk td{height:32px;padding:0 8px;border-bottom:1px solid var(--ant-color-border-secondary,#f0f0f0);border-inline-end:1px solid var(--ant-color-border-secondary,#f0f0f0);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;background:var(--ant-color-bg-container,#fff);cursor:cell}
.yc-bulk .lead,.yc-bulk .first{position:sticky;z-index:1}
.yc-bulk .lead{left:0;padding:0;text-align:center;cursor:default}
.yc-bulk .first{left:${LEAD}px}
.yc-bulk th.lead,.yc-bulk th.first{z-index:3}
.yc-bulk td.ro{color:var(--ant-color-text-tertiary,#999);background:var(--ant-color-fill-quaternary,#fafafa);cursor:not-allowed}
.yc-bulk td.dirty{background:#fff7e0;color:#262626}
.yc-bulk td.err{box-shadow:inset 0 0 0 1px #e34948;background:#fff1f0;color:#262626}
.yc-bulk td.focus{box-shadow:inset 0 0 0 2px #2357ff}
.yc-bulk td.edit{padding:0;overflow:visible}
.yc-bulk td input,.yc-bulk td select{width:100%;height:30px;border:0;outline:0;padding:0 7px;font:inherit;background:var(--ant-color-bg-elevated,#fff);color:inherit;box-shadow:inset 0 0 0 2px #2357ff}
.yc-bulk .mixed{color:var(--ant-color-text-tertiary,#999);font-style:italic}
@media(prefers-color-scheme:dark){:root:not([data-theme=light]) .yc-bulk td.dirty{background:#5c4a12;color:#fff}:root:not([data-theme=light]) .yc-bulk td.err{background:#5c1f1f;color:#fff}}
`;

const Row = memo(({ row, r, edits, errors, focusCol, editing, draft, setDraft, commit, cancel, onCell, onOpen, currency, labels }) => (
    <tr data-row={r}>
        <td className="lead">
            <Tooltip title={labels.open}><a onClick={() => onOpen(row.id)} aria-label={labels.open}><ExportOutlined style={{ fontSize: 12 }} /></a></Tooltip>
        </td>
        {COLS.map((c, ci) => {
            const f = c.key;
            const ro = readOnlyReason(row, f);
            const has = edits && f in edits;
            const err = errors?.[f];
            const focus = focusCol === ci;
            const mixed = !has && ((f === 'price' && row.price_mixed) || (f === 'compare_at_price' && row.compare_at_mixed));
            const cls = [ci === 0 ? 'first' : '', ro ? 'ro' : '', has ? 'dirty' : '', err ? 'err' : '', focus ? 'focus' : '', focus && editing ? 'edit' : ''].filter(Boolean).join(' ');
            const text = has ? edits[f] : cellText(row, f, currency);
            let body;
            if (focus && editing) {
                body = f === 'status'
                    ? (
                        <select autoFocus value={draft} onChange={(e) => { setDraft(e.target.value); commit(0, 0, e.target.value); }} onBlur={() => cancel()}>
                            {STATUSES.map((s) => <option key={s} value={s}>{labels.status[s]}</option>)}
                        </select>
                    )
                    : <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => commit(0, 0)} onFocus={(e) => { const n = e.target.value.length; e.target.setSelectionRange(n, n); }} />;
            } else if (f === 'status' && !err) body = <StatusTag value={text} style={{ margin: 0 }} />;
            else if (mixed) body = <span className="mixed">{labels.mixed}</span>;
            else if (ro === 'has_variants' && f === 'sku') body = labels.variants(row.variants_count);
            else if (ro === 'not_tracked') body = '∞';
            else body = text;
            return (
                <td key={f} data-cell={`${r}:${ci}`} className={cls} style={{ textAlign: c.align }} title={err || (ro === 'has_variants' ? labels.roVariants : ro === 'not_tracked' ? labels.roTracked : mixed ? labels.mixedHint : undefined)}
                    onMouseDown={(e) => { if (!(focus && editing)) { e.preventDefault(); onCell(r, ci); } }}>
                    {body}
                </td>
            );
        })}
    </tr>
));

const ProductsBulk = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { api, siteId, currency } = useSite();
    const [filters, setFilters] = useState({ q: '', status: undefined, page: 1 });
    const [q, setQ] = useState('');
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [edits, setEdits] = useState({});
    const [errors, setErrors] = useState({}); // {id: {field: message}}
    const [focus, setFocus] = useState({ r: 0, c: 0 });
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const box = useRef(null);
    const skipBlur = useRef(false);

    const count = useMemo(() => Object.values(edits).reduce((a, f) => a + Object.keys(f).length, 0), [edits]);
    useLeaveGuard(count > 0);
    useEffect(() => {
        if (!count) return undefined;
        const h = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', h);
        return () => window.removeEventListener('beforeunload', h);
    }, [count]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await api.get('/products/bulk', { page: filters.page, per_page: PER_PAGE, ...(filters.q ? { q: filters.q } : {}), ...(filters.status ? { status: filters.status } : {}) });
            setRows(listOf(d));
            setTotal(totalOf(d));
        } catch (e) { toastErr(e); setRows([]); setTotal(0); } finally { setLoading(false); }
    }, [api, filters]);
    useEffect(() => { load(); }, [load]);

    // always-current values for the handlers below, which stay stable for the memoized rows
    const st = useRef();
    st.current = { rows, edits, focus, editing, draft, currency };

    const setCell = useCallback((id, f, text, row) => {
        setEdits((all) => {
            const cur = { ...(all[id] || {}) };
            if (text === cellText(row, f, st.current.currency)) delete cur[f]; else cur[f] = text;
            const next = { ...all };
            if (Object.keys(cur).length) next[id] = cur; else delete next[id];
            return next;
        });
        setErrors((all) => {
            if (!all[id]?.[f]) return all;
            const cur = { ...all[id] };
            delete cur[f];
            return { ...all, [id]: cur };
        });
    }, []);

    const move = useCallback((dr, dc) => {
        setFocus((p) => {
            const n = st.current.rows.length;
            if (!n) return p;
            let c = p.c + dc;
            let r = p.r + dr;
            if (dc && c >= COLS.length) { c = 0; r += 1; } // Tab wraps to the next row
            if (dc && c < 0) { c = COLS.length - 1; r -= 1; }
            return { r: Math.min(n - 1, Math.max(0, r)), c: Math.min(COLS.length - 1, Math.max(0, c)) };
        });
    }, []);
    useEffect(() => {
        box.current?.querySelector(`[data-cell="${focus.r}:${focus.c}"]`)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, [focus]);
    const refocus = () => box.current?.focus({ preventScroll: true });

    const startEdit = useCallback((initial) => {
        const { rows: rs, edits: es, focus: fo, currency: cur } = st.current;
        const row = rs[fo.r];
        if (!row) return;
        const f = COLS[fo.c].key;
        if (readOnlyReason(row, f)) return;
        setDraft(initial ?? es[row.id]?.[f] ?? cellText(row, f, cur));
        setEditing(true);
    }, []);
    const commit = useCallback((dr, dc, value) => {
        if (skipBlur.current) { skipBlur.current = false; return; }
        const { rows: rs, focus: fo, draft: d, editing: on } = st.current;
        if (!on) return;
        const row = rs[fo.r];
        if (row) setCell(row.id, COLS[fo.c].key, value ?? d, row);
        setEditing(false);
        if (dr || dc) { skipBlur.current = true; move(dr, dc); }
        refocus();
        setTimeout(() => { skipBlur.current = false; }, 0);
    }, [move, setCell]);
    const cancel = useCallback(() => { skipBlur.current = true; setEditing(false); refocus(); setTimeout(() => { skipBlur.current = false; }, 0); }, []);

    const onCell = useCallback((r, c) => {
        const { focus: fo, editing: on } = st.current;
        if (on) commit(0, 0);
        if (fo.r === r && fo.c === c && !on) { startEdit(); return; } // second click/tap edits
        setFocus({ r, c });
        refocus();
    }, [commit, startEdit]);

    const onKeyDown = (e) => {
        if (editing) {
            if (e.key === 'Enter') { e.preventDefault(); commit(e.shiftKey ? -1 : 1, 0); } else if (e.key === 'Tab') { e.preventDefault(); commit(0, e.shiftKey ? -1 : 1); } else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
            return;
        }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); save(); return; } // eslint-disable-line no-use-before-define
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        const row = rows[focus.r];
        const f = COLS[focus.c]?.key;
        switch (e.key) {
            case 'ArrowUp': e.preventDefault(); move(-1, 0); return;
            case 'ArrowDown': e.preventDefault(); move(1, 0); return;
            case 'ArrowLeft': e.preventDefault(); move(0, -1); return;
            case 'ArrowRight': e.preventDefault(); move(0, 1); return;
            case 'Tab': e.preventDefault(); move(0, e.shiftKey ? -1 : 1); return;
            case 'Enter': case 'F2': e.preventDefault(); startEdit(); return;
            case 'Delete': case 'Backspace':
                e.preventDefault();
                if (row && !readOnlyReason(row, f)) setCell(row.id, f, '', row);
                return;
            default:
        }
        if (e.key.length === 1 && row && !readOnlyReason(row, f)) {
            e.preventDefault();
            if (f === 'status') {
                const m = STATUSES.find((s) => s.startsWith(e.key.toLowerCase()));
                if (m) setCell(row.id, f, m, row);
                return;
            }
            startEdit(e.key);
        }
    };

    const onPaste = (e) => {
        const txt = e.clipboardData?.getData('text/plain') ?? '';
        if (editing && !/[\t\n]/.test(txt.replace(/\r?\n$/, ''))) return; // a plain value goes into the open input
        e.preventDefault();
        if (editing) cancel();
        const grid = parseTsv(txt);
        let skipped = 0;
        let cut = 0;
        const statusNames = Object.fromEntries(STATUSES.map((s) => [t(s[0].toUpperCase() + s.slice(1)).toLowerCase(), s]));
        grid.forEach((cells, dr) => {
            const row = rows[focus.r + dr];
            if (!row) { cut += 1; return; }
            cells.forEach((text, dc) => {
                const col = COLS[focus.c + dc];
                if (!col) return;
                if (readOnlyReason(row, col.key)) { skipped += 1; return; }
                const v = col.key === 'status' ? (statusNames[text.trim().toLowerCase()] || text.trim().toLowerCase()) : text;
                setCell(row.id, col.key, v, row);
            });
        });
        if (skipped) message.info(t('{{n}} read-only cells skipped', { n: skipped }));
        if (cut) message.warning(t('{{n}} rows did not fit on this page', { n: cut }));
    };
    const onCopy = (e) => {
        if (editing) return;
        const row = rows[focus.r];
        if (!row) return;
        const f = COLS[focus.c].key;
        e.clipboardData.setData('text/plain', edits[row.id]?.[f] ?? cellText(row, f, currency));
        e.preventDefault();
    };

    const save = async () => {
        const items = [];
        const local = {};
        rows.forEach((row) => {
            const ed = edits[row.id];
            if (!ed) return;
            const fields = {};
            Object.entries(ed).forEach(([f, text]) => {
                const res = toApi(row, f, text, currency);
                if (res.code) (local[row.id] = local[row.id] || {})[f] = t(res.code);
                else fields[f] = res.value;
            });
            items.push({ id: row.id, fields });
        });
        if (Object.keys(local).length) {
            setErrors(local);
            message.error(t('Fix the marked cells'));
            return;
        }
        setSaving(true);
        try {
            const r = await api.patch('/products/bulk', { items }, QUIET);
            message.success(t('Updated {{n}} products', { n: r?.updated ?? items.length }));
            setEdits({});
            setErrors({});
            load();
        } catch (e) {
            const list = e?.body?.errors || e?.body?.data?.errors;
            if (Array.isArray(list) && list.length) {
                const map = {};
                list.forEach((x) => { const f = COLS.some((c) => c.key === x.field) ? x.field : 'title'; (map[x.id] = map[x.id] || {})[f] = x.message; });
                setErrors(map);
                message.error(t('Nothing was saved. Fix the marked cells'));
            } else toastErr(e, t('Save failed'));
        } finally { setSaving(false); }
    };

    const leave = (fn) => confirmLeave(() => { setEdits({}); setErrors({}); fn(); });
    const openProduct = useCallback((id) => confirmLeave(() => navigate(sitePath(siteId, `products/${id}`))), [navigate, siteId]);
    const labels = useMemo(() => ({
        open: t('Open product'), mixed: t('Mixed', { context: 'prices' }), mixedHint: t('Differs per variant. A new value applies to all'),
        variants: (n) => t('{{n}} variants', { n }), roVariants: t('This product has variants: edit it on the product page'), roTracked: t('Inventory is not tracked for this product'),
        status: { active: t('Active'), draft: t('Draft'), archived: t('Archived') },
    }), [t]);
    const nErr = Object.values(errors).reduce((a, f) => a + Object.keys(f).length, 0);
    const width = LEAD + COLS.reduce((a, c) => a + c.width, 0);
    const colLabel = { Type: t('Type'), Status: t('Status'), Title: t('Title') };

    return (
        <Card size="small"
            title={<Space><Button type="text" size="small" icon={<ArrowLeftOutlined />} onClick={() => confirmLeave(() => navigate(sitePath(siteId, 'products')))} />{t('Bulk edit')}</Space>}
            extra={(
                <Space>
                    {count > 0 && <Text type="secondary" style={{ fontSize: 12 }}>{t('{{n}} changes', { n: count })}</Text>}
                    {count > 0 && <Button size="small" onClick={() => { setEdits({}); setErrors({}); }}>{t('Discard')}</Button>}
                    <Button size="small" type="primary" disabled={!count} loading={saving} onClick={save}>{t('Save')}</Button>
                </Space>
            )}>
            <style>{STYLE}</style>
            <Space wrap style={{ marginBottom: 8 }}>
                <Input.Search allowClear size="small" placeholder={t('Search title, SKU, vendor')} value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 240 }}
                    onSearch={(v) => leave(() => setFilters((f) => ({ ...f, q: v, page: 1 })))} />
                <Select allowClear size="small" placeholder={t('Status')} style={{ width: 120 }} value={filters.status} onChange={(v) => leave(() => setFilters((f) => ({ ...f, status: v, page: 1 })))}
                    options={STATUSES.map((s) => ({ value: s, label: labels.status[s] }))} />
            </Space>
            {nErr > 0 && <Alert type="error" showIcon style={{ marginBottom: 8 }} message={t('Nothing was saved. Fix the marked cells')} />}
            <Spin spinning={loading}>
                {!loading && !rows.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No products match')} /> : (
                    <div className="yc-bulk" ref={box} tabIndex={0} role="grid" onKeyDown={onKeyDown} onPaste={onPaste} onCopy={onCopy}>
                        <table style={{ width }}>
                            <colgroup><col style={{ width: LEAD }} />{COLS.map((c) => <col key={c.key} style={{ width: c.width }} />)}</colgroup>
                            <thead>
                                <tr><th className="lead" />{COLS.map((c, i) => <th key={c.key} className={i === 0 ? 'first' : ''} style={{ textAlign: c.align }}>{colLabel[c.label] || t(c.label)}{c.money ? ` (${currency})` : ''}</th>)}</tr>
                            </thead>
                            <tbody>
                                {rows.map((row, r) => (
                                    <Row key={row.id} row={row} r={r} edits={edits[row.id]} errors={errors[row.id]} focusCol={focus.r === r ? focus.c : -1}
                                        editing={focus.r === r && editing} draft={focus.r === r && editing ? draft : ''} setDraft={setDraft} commit={commit} cancel={cancel}
                                        onCell={onCell} onOpen={openProduct} currency={currency} labels={labels} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Spin>
            {total > PER_PAGE && (
                <Pagination size="small" style={{ marginTop: 8, textAlign: 'right' }} current={filters.page} pageSize={PER_PAGE} total={total} showSizeChanger={false}
                    onChange={(page) => leave(() => { setFocus({ r: 0, c: 0 }); setFilters((f) => ({ ...f, page })); })} />
            )}
        </Card>
    );
};

export default ProductsBulk;
